import { useEffect, useMemo } from "react";
import { useCtrlKey } from "./use-is-mac";

// a hook which listens to a keyboard event
// when active is true the event is added to the reference element
// when the key is pressed (passed in from the parameter) the callback function is ran
export const useKeyboardEvent = (callback: (event: KeyboardEvent) => void, active: boolean, keyOrKeys: string | string[], element: HTMLElement | typeof document) => {
    useEffect(() => {
        if (element) {
            if (active) {
                element.addEventListener("keydown", eventListenerFunction);
            } else {
                element.removeEventListener("keydown", eventListenerFunction);
            }
        }
        return () => element?.removeEventListener("keydown", eventListenerFunction);
    }, [active, element]);

    const keys = useMemo(() => (Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]), [keyOrKeys]);

    const eventListenerFunction = (event: KeyboardEvent) => {
        if (keys.includes(event.key)) {
            callback(event);
        }
    };
};

/**
 * Checks if a key is being pressed with given modifiers
 * @param key - Key which needs to be pressed
 * @param modifiers - Supported modifiers are "Shift", "Control/Command"
 * @param onKeyboardShortPressed - Callback to be called once all conditions are met
 * @returns A string representation of the shortcut (e.g. "Ctrl + ⇧ + S")
 */
export function useKeyboardShortcut(key: string, modifiers: { shift: boolean; ctrl: boolean }, onKeyboardShortPressed: () => void) {
    useEffect(() => {
        const keyDown = (event: KeyboardEvent) => {
            // Ctrl for Windows and Command for MacOS
            const ctrlCheck = modifiers.ctrl === (event.metaKey || event.ctrlKey);
            const shiftCheck = modifiers.shift === event.shiftKey;
            if (event.key?.toLowerCase() === key && ctrlCheck && shiftCheck) {
                onKeyboardShortPressed();
            }
        };

        document.addEventListener("keydown", keyDown);

        return () => {
            document.removeEventListener("keydown", keyDown);
        };
    }, [onKeyboardShortPressed, key, modifiers.ctrl, modifiers.shift]);

    const ctrlKey = useCtrlKey();
    const parts = [];
    if (modifiers.ctrl) parts.push(ctrlKey);
    if (modifiers.shift) parts.push("⇧");
    parts.push(key.toUpperCase());
    return parts.join(" + ");
}
